import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import useApiData from "../hooks/useApiData";

const fallbackPricingPage = {
  hero: {
    badge: "Pricing Plans",
    title: "Choose Your Path",
    description: "Select the plan that matches your goals.",
  },
  plans: [],
};

export default function Pricing() {
  const {
    data: pageData,
    loading,
    error,
  } = useApiData("/pricing-plans", fallbackPricingPage);

  const handleApply = () => {
    window.location.href = "/contact";
  };

  // Safe access
  const plans = pageData?.plans || [];

  return (
    <div className="min-h-screen bg-[var(--dvx-bg)] pt-24 transition-colors duration-300">
      {/* HERO */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-[var(--dvx-primary)] font-oswald text-sm uppercase tracking-widest">
              {pageData?.hero?.badge || "Pricing"}
            </span>

            <h1 className="mt-4 font-oswald text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--dvx-text)] uppercase">
              {pageData?.hero?.title || "Our Plans"}
            </h1>

            {/* <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg max-w-2xl mx-auto">
              {pageData?.hero?.description || "Simple, transparent pricing."}
            </p> */}
          </motion.div>
        </div>
      </section>

      {/* PRICING */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* LOADING */}
          {loading && (
            <div className="flex justify-center py-20">
              <div className="font-oswald text-xl text-[var(--dvx-text)] uppercase tracking-widest animate-pulse">
                Loading plans...
              </div>
            </div>
          )}

          {/* ERROR */}
          {!loading && error && (
            <div className="text-center py-20">
              <p className="text-red-500 font-oswald uppercase">
                Failed to load pricing plans.
              </p>
              <p className="text-sm text-[var(--dvx-text-muted)] mt-2">
                Please try again later.
              </p>
            </div>
          )}

          {/* EMPTY STATE */}
          {!loading && !error && plans.length === 0 && (
            <div className="text-center py-20">
              <h3 className="font-oswald text-2xl text-[var(--dvx-text)] uppercase">
                No Plans Found
              </h3>
              <p className="text-[var(--dvx-text-muted)] mt-2">
                We currently don’t have any pricing plans available.
              </p>
            </div>
          )}

          {/* PLANS GRID */}
          {!loading && !error && plans.length > 0 && (
            <div className="grid md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <motion.div
                  key={plan.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative ${
                    plan.highlighted ? "md:-mt-4 md:mb-4" : ""
                  }`}
                >
                  {/* Highlight badge */}
                  {plan.highlighted && (
                    <div className="absolute -top-4 left-0 right-0 flex justify-center">
                      <span className="bg-[var(--dvx-primary)] text-white text-xs px-4 py-2 font-oswald uppercase">
                        Most chosen
                      </span>
                    </div>
                  )}

                  {/* Card */}
                  <div
                    className={`h-full p-8 ${
                      plan.highlighted
                        ? "bg-[var(--dvx-surface)] border-2 border-[var(--dvx-primary)]"
                        : "bg-[var(--dvx-surface)] border border-[var(--dvx-border)]"
                    }`}
                  >
                    {/* Name */}
                    <h3 className="font-oswald text-2xl font-bold text-[var(--dvx-text)] uppercase">
                      {plan.name || "Unnamed Plan"}
                    </h3>

                    {/* Description */}
                    <p className="mt-2 text-[var(--dvx-text-muted)] text-sm">
                      {plan.description || "No description available."}
                    </p>

                    {/* Price */}
                    <div
                      className={`mt-4 font-oswald text-[var(--dvx-text)] font-bold`}
                    >
                      {plan.amount ? (
                        <span className="text-3xl">
                          {plan.currency || "$"}{" "}
                          {parseFloat(plan.amount).toFixed(0)}
                          <span className="text-sm font-manrope text-[var(--dvx-text-muted)]">
                            /mo
                          </span>
                        </span>
                      ) : (
                        <span className="text-sm">
                          Will be discussed based on the client’s timeline and
                          goals.
                        </span>
                      )}
                    </div>

                    {/* Features */}
                    <ul className="mt-8 space-y-4">
                      {(plan.features || []).length > 0 ? (
                        plan.features.map((feature, featureIndex) => (
                          <li
                            key={`${feature}-${featureIndex}`}
                            className="flex gap-3 text-[var(--dvx-text-muted)]"
                          >
                            <Check
                              size={18}
                              className="text-[var(--dvx-primary)] mt-1"
                            />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))
                      ) : (
                        <li className="text-sm text-[var(--dvx-text-muted)]">
                          No features listed.
                        </li>
                      )}
                    </ul>

                    {/* CTA */}
                    <Button
                      onClick={handleApply}
                      className={`w-full mt-8 py-4 font-oswald uppercase ${
                        plan.highlighted
                          ? "bg-[var(--dvx-primary)] text-white"
                          : "border border-[var(--dvx-border)]"
                      }`}
                    >
                      {plan.cta_text || "Apply"}
                      <ArrowRight className="ml-2" size={18} />
                    </Button>

                    {/* Footer note */}
                    <p className="mt-4 text-xs text-[var(--dvx-text-muted)]">
                      We love clients who can execute.
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
